import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";

const TILE_SIZE = 130;

const PdfUploadTile = ({
  file,
  onChange,
  isDisabled = false,
  fallbackName = "Contract.pdf",
}) => {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [hover, setHover] = useState(false);
  const hasFile = !!file;

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }
    if (typeof file === "string") {
      setPreviewUrl(file);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const fileName =
    typeof file === "string"
      ? file.split("/").pop() || fallbackName
      : file?.name || fallbackName;

  const handleSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) onChange?.(f);
    e.target.value = "";
  };

  const triggerInput = () => {
    if (isDisabled || hasFile) return;
    inputRef.current?.click();
  };

  const handlePreview = () => {
    if (!previewUrl) return;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDelete = (e) => {
    e?.stopPropagation?.();
    if (isDisabled) return;
    onChange?.(null);
  };

  return (
    <>
      {hasFile ? (
        <Box
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={handlePreview}
          sx={{
            width: TILE_SIZE,
            height: TILE_SIZE,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDisabled ? "not-allowed" : "zoom-in",
            overflow: "hidden",
            transition: "border-color 0.2s ease",
            "&:hover": !isDisabled && {
              borderColor: "#4D6BF6",
            },
          }}
          title={fileName}
        >
          <PdfIcon style={{ width: 44, height: 44 }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: "#374151",
              mt: 0.75,
              px: 1,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fileName}
          </Typography>
          {!isDisabled && hover && (
            <IconButton
              size="small"
              onClick={handleDelete}
              title="Delete"
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                backgroundColor: "rgba(255,255,255,0.95)",
                color: "#DC2626",
                "&:hover": { backgroundColor: "#fff" },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box
          onClick={triggerInput}
          sx={{
            width: TILE_SIZE,
            height: TILE_SIZE,
            borderRadius: "12px",
            border: "1px dashed #9CA3AF",
            backgroundColor: "#F9FAFB",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDisabled ? "not-allowed" : "pointer",
            transition: "border-color 0.2s ease",
            "&:hover": !isDisabled && {
              borderColor: "#4D6BF6",
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0D0D0E",
              mb: 0.75,
            }}
          >
            <AddIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            Upload PDF
          </Typography>
        </Box>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleSelect}
      />
    </>
  );
};

const UploadSection = ({ title, helper, file, onChange, isDisabled, fallbackName }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2.5,
      borderRadius: "12px",
      backgroundColor: "#fff",
      flexWrap: { xs: "wrap", sm: "nowrap" },
      marginBottom: 2,
    }}
  >
    <PdfUploadTile
      file={file}
      onChange={onChange}
      isDisabled={isDisabled}
      fallbackName={fallbackName}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "#0D0D0E",
          mb: 0.5,
          fontFamily: "Inter Tight",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          color: "#6B7280",
          fontFamily: "Inter Tight",
        }}
      >
        {helper}
      </Typography>
    </Box>
  </Box>
);

const Contract = ({
  contract,
  onChange,
  renoContract,
  onChangeRenoContract,
  isDisabled = false,
}) => {
  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "1vh 0",
        border: "1px solid #E5E7EB",
      }}
    >
      <CardContent sx={{ padding: "20px" }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0D0D0E",
            mb: 2,
            fontFamily: "Inter Tight",
          }}
        >
          Contract
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <UploadSection
            title="Contract"
            helper="Upload the signed contract PDF."
            file={contract}
            onChange={onChange}
            isDisabled={isDisabled}
            fallbackName="Contract.pdf"
          />
          <UploadSection
            title="Reno Contract"
            helper="Upload the Reno contract PDF."
            file={renoContract}
            onChange={onChangeRenoContract}
            isDisabled={isDisabled}
            fallbackName="RenoContract.pdf"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default Contract;
